// src/pages/Menu/Vinculation/panel/MailTemplatesModal.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Mail, XCircle, Search, Loader2, Send, RotateCcw, ChevronDown, ChevronUp, Save, Settings, FileText } from 'lucide-react';
import Swal from 'sweetalert2';
import TooltipHelper from '../../../../components/common/TooltipHelper';

import { useMailDrafts } from './MailDraftsContext';
import {
  buildAcceptDraft,
  buildRejectDraft,
  buildAcceptanceDraft,
  buildReceivedDraft,
  pickBestTo,
  renderMailDraft,
  hasGeneratedPdf,
} from '../../../../services/mailTemplatesService';

import {
  sendMembershipMail,
  getMailTemplates,
  saveMailTemplate,
  deleteMailTemplate,
  getCcDefault,
} from '../../../../services/membershipmailsService';
import { getMembershipConfigs, updateMembershipConfig, uploadPresentationFile, getPresentationFile, deletePresentationFile } from '../../../../services/membershipConfigService';

import {
  listMembershipApplications,
  setMembershipApplicationStatus,
} from '../../../../services/membershipApplications';

import { sendEmail } from '../../../../services/mailService';

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export default function MailTemplatesModal({ open, onClose }) {
  const { acceptDraft, setAcceptDraft, rejectDraft, setRejectDraft, acceptanceDraft, setAcceptanceDraft, receivedDraft, setReceivedDraft } = useMailDrafts();

  // pestañas
  const [tab, setTab] = useState('SEND'); // 'SEND' | 'TEMPLATES' | 'CONFIG' | 'PRESENTATION'

  // bloqueo de edición de plantillas
  const [isEditingUnlocked, setIsEditingUnlocked] = useState(false);
  
  // bloqueo de edición de configuraciones
  const [isConfigEditingUnlocked, setIsConfigEditingUnlocked] = useState(false);
  
  // modo de edición: 'simple' | 'advanced'
  const [editMode, setEditMode] = useState('simple');

  // Control de expansión de plantillas
  const [expandedTemplates, setExpandedTemplates] = useState({
    received: false,
    accept: false,
    reject: false,
    acceptance: false,
  });

  // Control de expansión de configuraciones
  const [expandedConfigs, setExpandedConfigs] = useState({
    notify: false,
    reply: false,
    cc: false,
  });

  // edición plantillas
  const [localAccept, setLocalAccept] = useState(acceptDraft || buildAcceptDraft({}));
  const [localReject, setLocalReject] = useState(rejectDraft || buildRejectDraft({}));
  const [localAcceptance, setLocalAcceptance] = useState(acceptanceDraft || buildAcceptanceDraft({}));
  const [localReceived, setLocalReceived] = useState(receivedDraft || buildReceivedDraft({}));
  
  // Checkbox para adjuntar presentación en plantilla RECEIVED
  const [receivedAttachPresentation, setReceivedAttachPresentation] = useState(false);
  
  // Estado para el archivo de presentación actual
  const [presentationFileUrl, setPresentationFileUrl] = useState('');
  const [loadingPresentation, setLoadingPresentation] = useState(false);

  // envío manual
  const [qSend, setQSend] = useState('');
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const [to, setTo] = useState('');
  const [ccLine, setCcLine] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copyToMe, setCopyToMe] = useState(true);
  const [attachDeclaration, setAttachDeclaration] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const [nextStatus, setNextStatus] = useState(''); // '', 'RECEIVED', 'ACCEPTED', 'REJECTED'
  const [sending, setSending] = useState(false);
  const [toError, setToError] = useState('');
  const [ccLineError, setCcLineError] = useState('');

  // Referencias para los campos
  const acceptSubjectRef = useRef(null);
  const acceptBodyRef = useRef(null);
  const rejectSubjectRef = useRef(null);
  const rejectBodyRef = useRef(null);
  const acceptanceSubjectRef = useRef(null);
  const acceptanceBodyRef = useRef(null);
  const receivedSubjectRef = useRef(null);
  const receivedBodyRef = useRef(null);
  
  // Referencia al campo activo para insertar tokens
  const activeFieldRef = useRef(null);
  
  // Referencia para guardar posición del cursor antes del cambio
  const cursorPosBeforeChange = useRef({
    subject: 0,
    body: 0,
    rejectSubject: 0,
    rejectBody: 0,
    acceptanceSubject: 0,
    acceptanceBody: 0,
    receivedSubject: 0,
    receivedBody: 0,
  });

  // Configuración de correos
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState({});
  const [editedConfigValues, setEditedConfigValues] = useState({});

  // Función para detectar si una posición está dentro de una variable
  const isPositionInVariable = (text, position) => {
    if (!text || position === null || position === undefined) return false;
    const matches = [...text.matchAll(/\{\{\s*\w+\s*\}\}/g)];
    return matches.some(match => {
      const start = match.index;
      const end = start + match[0].length;
      return position >= start && position < end;
    });
  };

  // Función para proteger variables al editar en modo simple
  const handleSimpleModeEdit = (originalText, newText, cursorPos, fieldRef) => {
    if (!originalText || editMode !== 'simple') return newText;
    
    // Extraer todas las variables del texto original
    const variableMatches = [...originalText.matchAll(/\{\{\s*\w+\s*\}\}/g)];
    
    if (variableMatches.length === 0) {
      // No hay variables, permitir cualquier cambio
      return newText;
    }

    // Verificar si el cursor está dentro de una variable ANTES del cambio
    // Usar la posición guardada antes del cambio si está disponible
    const checkPos = cursorPos !== null && cursorPos !== undefined ? cursorPos : 0;
    if (isPositionInVariable(originalText, checkPos)) {
      // Si está dentro de una variable, no permitir cambios
      return originalText;
    }

    // Extraer todas las variables del nuevo texto
    const newVariableMatches = [...newText.matchAll(/\{\{\s*\w+\s*\}\}/g)];
    
    // Crear arrays con las variables completas (formato exacto)
    const originalVars = variableMatches.map(m => m[0]); // Formato completo: {{variable}}
    const newVars = newVariableMatches.map(m => m[0]);
    
    // Extraer los nombres de las variables (sin los corchetes) para comparar
    const originalVarNames = originalVars.map(v => {
      const match = v.match(/\{\{\s*(\w+)\s*\}\}/);
      return match ? match[1] : null;
    }).filter(Boolean);
    
    const newVarNames = newVars.map(v => {
      const match = v.match(/\{\{\s*(\w+)\s*\}\}/);
      return match ? match[1] : null;
    }).filter(Boolean);
    
    // Si se está insertando texto (nuevo texto es más largo), ser más permisivo
    const isInserting = newText.length > originalText.length;
    
    // Verificar que todas las variables originales estén presentes (por nombre)
    // Permitir que se inserten caracteres entre variables (como espacios)
    // Contar ocurrencias de cada variable
    const originalVarCounts = {};
    originalVarNames.forEach(name => {
      originalVarCounts[name] = (originalVarCounts[name] || 0) + 1;
    });
    
    const newVarCounts = {};
    newVarNames.forEach(name => {
      newVarCounts[name] = (newVarCounts[name] || 0) + 1;
    });
    
    // Verificar que cada variable original esté presente al menos la misma cantidad de veces
    for (const varName in originalVarCounts) {
      const originalCount = originalVarCounts[varName];
      const newCount = newVarCounts[varName] || 0;
      
      // Si hay menos ocurrencias de esta variable, fue eliminada
      if (newCount < originalCount) {
        return originalText;
      }
    }

    // Si todas las variables están presentes (o hay más), permitir el cambio
    // Los tokens se desplazarán automáticamente cuando se inserte texto cerca de ellos
    return newText;
  };

  // Función para insertar token en un campo
  const insertToken = (token, fieldRef, setter) => {
    if (!fieldRef.current) return;
    
    const field = fieldRef.current;
    const start = field.selectionStart || 0;
    const end = field.selectionEnd || 0;
    const value = field.value || '';
    const newValue = value.substring(0, start) + token + value.substring(end);
    
    setter(newValue);
    
    // Restaurar posición del cursor
    setTimeout(() => {
      field.focus();
      field.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  // Cargar configuraciones de correos
  const loadEmailConfigs = async () => {
    try {
      setConfigLoading(true);
      const response = await getMembershipConfigs();
      if (response.ok && response.data) {
        const configsData = response.data.filter(config => 
          ['MEMBERSHIP_NOTIFY_TO', 'MEMBERSHIP_REPLY_TO', 'MEMBERSHIP_CC_DEFAULT'].includes(config.key)
        );
        setEmailConfigs(configsData);
        const initialValues = {};
        configsData.forEach(config => {
          initialValues[config.key] = config.value || '';
        });
        setEditedConfigValues(initialValues);
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al cargar configuraciones', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleConfigValueChange = (key, value) => {
    setEditedConfigValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmailList = (value) => {
    if (!value || value.trim() === '') return true;
    const emails = value.split(',').map(e => e.trim()).filter(Boolean);
    return emails.every(email => validateEmail(email));
  };

  const handleSaveConfig = async (config) => {
    const newValue = editedConfigValues[config.key] || '';
    
    if (config.key === 'MEMBERSHIP_REPLY_TO') {
      if (newValue.trim() && !validateEmail(newValue.trim())) {
        Swal.fire('Error', 'Por favor ingresa un email válido', 'error');
        return;
      }
    } else if (config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT') {
      if (newValue.trim() && !validateEmailList(newValue)) {
        Swal.fire('Error', 'Por favor ingresa emails válidos separados por comas', 'error');
        return;
      }
    }

    try {
      setConfigSaving(prev => ({ ...prev, [config.key]: true }));
      await updateMembershipConfig(config.key, newValue.trim(), config.description);
      
      await Swal.fire({
        icon: 'success',
        title: 'Configuración actualizada',
        text: 'La configuración se guardó correctamente',
        timer: 1500,
        showConfirmButton: false,
      });

      await loadEmailConfigs();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al actualizar configuración', 'error');
    } finally {
      setConfigSaving(prev => ({ ...prev, [config.key]: false }));
    }
  };

  const getConfigLabel = (key) => {
    const labels = {
      'MEMBERSHIP_NOTIFY_TO': 'Email de Notificación',
      'MEMBERSHIP_REPLY_TO': 'Email de Respuesta (Reply-To)',
      'MEMBERSHIP_CC_DEFAULT': 'Emails en CC por Defecto',
    };
    return labels[key] || key;
  };

  const getConfigPlaceholder = (key) => {
    const placeholders = {
      'MEMBERSHIP_NOTIFY_TO': 'ejemplo@cidere.cl, otro@cidere.cl',
      'MEMBERSHIP_REPLY_TO': 'respuesta@cidere.cl',
      'MEMBERSHIP_CC_DEFAULT': 'cc1@cidere.cl, cc2@cidere.cl',
    };
    return placeholders[key] || '';
  };

  // Cargar configuraciones cuando se abre la pestaña CONFIG
  useEffect(() => {
    if (open && tab === 'CONFIG') {
      loadEmailConfigs();
    }
  }, [open, tab]);

  // Colapsar configuraciones al cambiar de pestaña
  useEffect(() => {
    if (tab !== 'CONFIG') {
      setExpandedConfigs({
        notify: false,
        reply: false,
        cc: false,
      });
    }
  }, [tab]);

  // Cargar preferencia de adjuntar presentación en RECEIVED
  useEffect(() => {
    if (open) {
      const loadReceivedPresentationPreference = async () => {
        try {
          const response = await getMembershipConfigs();
          if (response.ok && response.data) {
            const config = response.data.find(c => c.key === 'MEMBERSHIP_RECEIVED_ATTACH_PRESENTATION');
            setReceivedAttachPresentation(config?.value === 'true' || false);
          }
        } catch (error) {
          console.error('Error al cargar preferencia de presentación:', error);
        }
      };
      loadReceivedPresentationPreference();
    }
  }, [open]);

  // Cargar archivo de presentación cuando se abre la pestaña PRESENTATION
  useEffect(() => {
    if (open && tab === 'PRESENTATION') {
      const loadPresentationFile = async () => {
        setLoadingPresentation(true);
        try {
          const response = await getPresentationFile();
          if (response.ok && response.data?.url) {
            setPresentationFileUrl(response.data.url);
          } else {
            setPresentationFileUrl('');
          }
        } catch (error) {
          console.error('Error al cargar archivo de presentación:', error);
          setPresentationFileUrl('');
        } finally {
          setLoadingPresentation(false);
        }
      };
      loadPresentationFile();
    }
  }, [open, tab]);

  // reset al abrir la ventana superpuesta
  useEffect(() => {
    if (!open) {
      // Al cerrar el modal, cerrar todos los desplegables
      setExpandedTemplates({
        received: false,
        accept: false,
        reject: false,
        acceptance: false,
      });
      return;
    }
    setTab('SEND');
    setIsEditingUnlocked(false);
    setIsConfigEditingUnlocked(false);
    setEditMode('simple');
    setLocalAccept(acceptDraft || buildAcceptDraft({}));
    setLocalReject(rejectDraft || buildRejectDraft({}));
    setLocalAcceptance(acceptanceDraft || buildAcceptanceDraft({}));
    setLocalReceived(receivedDraft || buildReceivedDraft({}));
    // limpiar envío manual
    setQSend(''); setApps([]); setSelectedApp(null);
    setTo(''); setCcLine(''); setSubject(''); setBody('');
    setCopyToMe(true); setAttachDeclaration(false); setAttachPdf(false);
    setNextStatus('');
    setToError(''); setCcLineError('');
    activeFieldRef.current = null;
    // Cerrar todos los desplegables al abrir
    setExpandedTemplates({
      received: false,
      accept: false,
      reject: false,
      acceptance: false,
    });
    setExpandedConfigs({
      notify: false,
      reply: false,
      cc: false,
    });
  }, [open]); // Solo cuando se abre/cierra la ventana superpuesta

  // Actualizar valores locales cuando cambian los drafts (sin resetear la pestaña)
  useEffect(() => {
    if (!open) return;
    setLocalAccept(acceptDraft || buildAcceptDraft({}));
    setLocalReject(rejectDraft || buildRejectDraft({}));
    setLocalAcceptance(acceptanceDraft || buildAcceptanceDraft({}));
    setLocalReceived(receivedDraft || buildReceivedDraft({}));
  }, [acceptDraft, rejectDraft, acceptanceDraft, receivedDraft, open]);

  // Función para desbloquear edición
  const unlockEditing = async () => {
    const { value } = await Swal.fire({
      title: 'Desbloquear edición',
      text: 'Escribe "editar" para desbloquear la edición de plantillas:',
      input: 'text',
      inputPlaceholder: 'Escribe "editar"',
      inputAttributes: {
        autocomplete: 'off',
      },
      showCancelButton: true,
      confirmButtonText: 'Desbloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0488AF',
      inputValidator: (value) => {
        if (!value || value.toLowerCase().trim() !== 'editar') {
          return 'Debes escribir "editar" para continuar';
        }
      },
    });

    if (value && value.toLowerCase().trim() === 'editar') {
      setIsEditingUnlocked(true);
      Swal.fire('Desbloqueado', 'Ahora puedes editar las plantillas.', 'success');
    }
  };

  const unlockConfigEditing = async () => {
    const { value } = await Swal.fire({
      title: 'Desbloquear edición',
      text: 'Escribe "editar" para desbloquear la edición de configuraciones:',
      input: 'text',
      inputPlaceholder: 'Escribe "editar"',
      inputAttributes: {
        autocomplete: 'off',
      },
      showCancelButton: true,
      confirmButtonText: 'Desbloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0488AF',
      inputValidator: (value) => {
        if (!value || value.toLowerCase().trim() !== 'editar') {
          return 'Debes escribir "editar" para continuar';
        }
      },
    });

    if (value && value.toLowerCase().trim() === 'editar') {
      setIsConfigEditingUnlocked(true);
      Swal.fire('Desbloqueado', 'Ahora puedes editar las configuraciones.', 'success');
    }
  };

  // buscar postulaciones en pestaña Enviar (solo pendientes)
  useEffect(() => {
    if (!open || tab !== 'SEND') return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await listMembershipApplications({ q: qSend, status: 'RECEIVED', page: 1, pageSize: 8 });
        setApps(res.data || []);
      } catch (e) {
        Swal.fire('Error', e?.message || 'No se pudo cargar postulaciones', 'error');
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [qSend, tab, open]);

  // al escoger app -> autocompletar "Para" y dejar asunto/cuerpo libres
  useEffect(() => {
    if (!selectedApp) return;
    setTo(pickBestTo(selectedApp) || '');
  }, [selectedApp]);

  // Ya no necesitamos verificar si hay PDF disponible, siempre se puede adjuntar la presentación
  // const pdfAvailable = useMemo(() => hasGeneratedPdf(selectedApp || {}), [selectedApp]);

  // Tokens disponibles
  const availableTokens = [
    { token: '{{empresa}}', label: 'Nombre empresa', required: true, description: 'Nombre de la empresa (se inserta automáticamente si no se usa)' },
    { token: '{{rut}}', label: 'RUT empresa', required: false, description: 'RUT de la empresa' },
    { token: '{{contactoNombre}}', label: 'Contacto empresa', required: false, description: 'Nombre del contacto de la empresa' },
    { token: '{{fecha}}', label: 'Fecha actual', required: false, description: 'Fecha actual' },
    { token: '{{fechaPostulacion}}', label: 'Fecha de postulación', required: false, description: 'Fecha en que se realizó la postulación' },
  ];

  // restaurar plantilla de aceptación al estado original
  const restoreAcceptTemplate = async () => {
    const result = await Swal.fire({
      title: '¿Restaurar plantilla de Aceptación?',
      text: 'Esto restaurará la plantilla de Aceptación a su estado original. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMailTemplate('ACCEPTED');
        const defaultAccept = buildAcceptDraft();
        setLocalAccept(defaultAccept);
        setAcceptDraft(defaultAccept);
        Swal.fire('Restaurado', 'La plantilla de Aceptación se ha restaurado a su estado original.', 'success');
      } catch (error) {
        console.error('Error al restaurar plantilla:', error);
        Swal.fire('Error', error?.message || 'No se pudo restaurar la plantilla.', 'error');
      }
    }
  };

  // restaurar plantilla de rechazo al estado original
  const restoreRejectTemplate = async () => {
    const result = await Swal.fire({
      title: '¿Restaurar plantilla de Rechazo?',
      text: 'Esto restaurará la plantilla de Rechazo a su estado original. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMailTemplate('REJECTED');
        const defaultReject = buildRejectDraft();
        setLocalReject(defaultReject);
        setRejectDraft(defaultReject);
        Swal.fire('Restaurado', 'La plantilla de Rechazo se ha restaurado a su estado original.', 'success');
      } catch (error) {
        console.error('Error al restaurar plantilla:', error);
        Swal.fire('Error', error?.message || 'No se pudo restaurar la plantilla.', 'error');
      }
    }
  };

  // restaurar plantilla de aceptación final al estado original
  const restoreAcceptanceTemplate = async () => {
    const result = await Swal.fire({
      title: '¿Restaurar plantilla de Aceptación Final?',
      text: 'Esto restaurará la plantilla de Aceptación Final a su estado original. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMailTemplate('ACCEPTANCE');
        const defaultAcceptance = buildAcceptanceDraft();
        setLocalAcceptance(defaultAcceptance);
        setAcceptanceDraft(defaultAcceptance);
        Swal.fire('Restaurado', 'La plantilla de Aceptación Final se ha restaurado a su estado original.', 'success');
      } catch (error) {
        console.error('Error al restaurar plantilla:', error);
        Swal.fire('Error', error?.message || 'No se pudo restaurar la plantilla.', 'error');
      }
    }
  };

  // restaurar plantilla de recepción al estado original
  const restoreReceivedTemplate = async () => {
    const result = await Swal.fire({
      title: '¿Restaurar plantilla de Recepción?',
      text: 'Esto restaurará la plantilla de Recepción de Postulación a su estado original. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMailTemplate('RECEIVED');
        const defaultReceived = buildReceivedDraft();
        setLocalReceived(defaultReceived);
        setReceivedDraft(defaultReceived);
        Swal.fire('Restaurado', 'La plantilla de Recepción se ha restaurado a su estado original.', 'success');
      } catch (error) {
        console.error('Error al restaurar plantilla:', error);
        Swal.fire('Error', error?.message || 'No se pudo restaurar la plantilla.', 'error');
      }
    }
  };

  // Función para detectar si hay cambios sin guardar
  const hasUnsavedChanges = () => {
    const acceptData = {
      subject: (localAccept?.subject || '').trim(),
      body: (localAccept?.body || '').trim(),
    };
    const rejectData = {
      subject: (localReject?.subject || '').trim(),
      body: (localReject?.body || '').trim(),
    };
    const acceptanceData = {
      subject: (localAcceptance?.subject || '').trim(),
      body: (localAcceptance?.body || '').trim(),
    };
    const receivedData = {
      subject: (localReceived?.subject || '').trim(),
      body: (localReceived?.body || '').trim(),
    };

    const acceptChanged = 
      acceptData.subject !== (acceptDraft?.subject || '').trim() ||
      acceptData.body !== (acceptDraft?.body || '').trim();
    
    const rejectChanged = 
      rejectData.subject !== (rejectDraft?.subject || '').trim() ||
      rejectData.body !== (rejectDraft?.body || '').trim();
    
    const acceptanceChanged = 
      acceptanceData.subject !== (acceptanceDraft?.subject || '').trim() ||
      acceptanceData.body !== (acceptanceDraft?.body || '').trim();
    
    const receivedChanged = 
      receivedData.subject !== (receivedDraft?.subject || '').trim() ||
      receivedData.body !== (receivedDraft?.body || '').trim();

    return acceptChanged || rejectChanged || acceptanceChanged || receivedChanged;
  };

  // Manejar cierre de la ventana superpuesta con confirmación si hay cambios
  const handleClose = async () => {
    if (hasUnsavedChanges()) {
      const result = await Swal.fire({
        title: '¿Guardar cambios?',
        text: 'Tienes cambios sin guardar. ¿Deseas guardarlos antes de cerrar?',
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        denyButtonColor: '#dc2626',
        confirmButtonText: 'Guardar y cerrar',
        denyButtonText: 'Cerrar sin guardar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        // Guardar y cerrar
        await saveTemplates();
        setIsEditingUnlocked(false);
        onClose?.();
      } else if (result.isDenied) {
        // Cerrar sin guardar
        setIsEditingUnlocked(false);
        onClose?.();
      }
      // Si es cancelado, no hacer nada
    } else {
      // No hay cambios, cerrar directamente
      setIsEditingUnlocked(false);
      onClose?.();
    }
  };

  // guardar plantillas (todas)
  const saveTemplates = async () => {
    try {
      const acceptData = {
        subject: (localAccept?.subject || '').trim(),
        body: (localAccept?.body || '').trim(),
      };
      const rejectData = {
        subject: (localReject?.subject || '').trim(),
        body: (localReject?.body || '').trim(),
      };
      const acceptanceData = {
        subject: (localAcceptance?.subject || '').trim(),
        body: (localAcceptance?.body || '').trim(),
      };
      const receivedData = {
        subject: (localReceived?.subject || '').trim(),
        body: (localReceived?.body || '').trim(),
      };

      // Guardar en el backend
      await Promise.all([
        saveMailTemplate('ACCEPTED', acceptData.subject, acceptData.body),
        saveMailTemplate('REJECTED', rejectData.subject, rejectData.body),
        saveMailTemplate('ACCEPTANCE', acceptanceData.subject, acceptanceData.body),
        saveMailTemplate('RECEIVED', receivedData.subject, receivedData.body),
        // Guardar preferencia de adjuntar presentación
        updateMembershipConfig('MEMBERSHIP_RECEIVED_ATTACH_PRESENTATION', receivedAttachPresentation ? 'true' : 'false', 'Adjuntar ficha de presentación de CIDERE en correos de recepción de postulación'),
      ]);

      // Actualizar el contexto
      setAcceptDraft(acceptData);
      setRejectDraft(rejectData);
      setAcceptanceDraft(acceptanceData);
      setReceivedDraft(receivedData);

      // Contraer todas las plantillas después de guardar
      setExpandedTemplates({
        received: false,
        accept: false,
        reject: false,
        acceptance: false,
      });

      // Cerrar el modo de edición después de guardar
      setIsEditingUnlocked(false);

      Swal.fire('Guardado', 'Las plantillas se han guardado permanentemente.', 'success');
      // No cerrar la ventana superpuesta, permitir seguir editando
    } catch (error) {
      console.error('Error al guardar plantillas:', error);
      Swal.fire('Error', error?.message || 'No se pudieron guardar las plantillas.', 'error');
    }
  };

  // Guardar plantilla individual
  const saveSingleTemplate = async (templateType, templateName) => {
    try {
      let data;
      let setter;
      
      switch (templateType) {
        case 'RECEIVED':
          data = {
            subject: (localReceived?.subject || '').trim(),
            body: (localReceived?.body || '').trim(),
          };
          setter = setReceivedDraft;
          // Guardar también la preferencia de adjuntar presentación
          await updateMembershipConfig('MEMBERSHIP_RECEIVED_ATTACH_PRESENTATION', receivedAttachPresentation ? 'true' : 'false', 'Adjuntar ficha de presentación de CIDERE en correos de recepción de postulación');
          break;
        case 'ACCEPTED':
          data = {
            subject: (localAccept?.subject || '').trim(),
            body: (localAccept?.body || '').trim(),
          };
          setter = setAcceptDraft;
          break;
        case 'REJECTED':
          data = {
            subject: (localReject?.subject || '').trim(),
            body: (localReject?.body || '').trim(),
          };
          setter = setRejectDraft;
          break;
        case 'ACCEPTANCE':
          data = {
            subject: (localAcceptance?.subject || '').trim(),
            body: (localAcceptance?.body || '').trim(),
          };
          setter = setAcceptanceDraft;
          break;
        default:
          throw new Error('Tipo de plantilla no válido');
      }

      // Guardar en el backend
      await saveMailTemplate(templateType, data.subject, data.body);

      // Actualizar el contexto
      setter(data);

      // Contraer la plantilla guardada
      setExpandedTemplates(prev => ({
        ...prev,
        received: templateType === 'RECEIVED' ? false : prev.received,
        accept: templateType === 'ACCEPTED' ? false : prev.accept,
        reject: templateType === 'REJECTED' ? false : prev.reject,
        acceptance: templateType === 'ACCEPTANCE' ? false : prev.acceptance,
      }));

      // Salir del modo de edición cuando se cierre el modal (automáticamente o manualmente)
      await Swal.fire({
        title: 'Guardado',
        text: `La plantilla "${templateName}" se ha guardado correctamente.`,
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        didClose: () => {
          setIsEditingUnlocked(false);
        }
      });
      
      // Asegurar que se salga del modo de edición si se cierra por timer
      setIsEditingUnlocked(false);
    } catch (error) {
      console.error(`Error al guardar plantilla ${templateName}:`, error);
      Swal.fire('Error', error?.message || `No se pudo guardar la plantilla "${templateName}".`, 'error');
    }
  };

  // enviar (manual)
  const sendFromTemplates = async () => {
    // Validar email "to"
    if (!to || !to.trim()) {
      setToError('El campo de destinatario es requerido');
      Swal.fire('Correo inválido', 'Completa un destinatario válido.', 'warning');
      return;
    }
    if (!emailRx.test(to.trim())) {
      setToError('El formato del email no es válido. Debe contener @ y un dominio (ej: correo@empresa.cl)');
      Swal.fire('Correo inválido', 'El formato del email no es válido. Debe contener @ y un dominio (ej: correo@empresa.cl)', 'warning');
      return;
    }
    setToError('');
    
    // Validar emails en CC si hay
    if (ccLine && ccLine.trim()) {
      const ccEmails = ccLine.split(',').map(e => e.trim()).filter(Boolean);
      const invalidEmails = ccEmails.filter(email => !emailRx.test(email));
      if (invalidEmails.length > 0) {
        setCcLineError(`Los siguientes emails no tienen formato válido: ${invalidEmails.join(', ')}. Deben contener @ y un dominio.`);
        Swal.fire('Correo inválido', `Los siguientes emails en CC no tienen formato válido: ${invalidEmails.join(', ')}. Deben contener @ y un dominio.`, 'warning');
        return;
      }
    }
    setCcLineError('');
    
    if (!subject.trim() || !body.trim()) {
      Swal.fire('Faltan datos', 'Completa asunto y cuerpo del correo.', 'warning');
      return;
    }

    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${to.trim()}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    try {
      setSending(true);

      // Mostrar mensaje de carga
      Swal.fire({
        title: 'Enviando...',
        text: 'Por favor espera mientras se envía el correo.',
        icon: 'info',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Si hay postulación seleccionada, usar el servicio de membresía
      if (selectedApp?.id) {
        await sendMembershipMail(selectedApp.id, {
          template: 'RECEIVED',
          to: to.trim(),
          cc: ccLine,
          subject: subject.trim(),
          body: body.trim(),
          copyToMe,
          attachDeclaration: !!attachDeclaration,
          attachApplicationPdf: !!attachPdf,
        });

        if (nextStatus) {
          await setMembershipApplicationStatus(selectedApp.id, nextStatus);
        }
      } else {
        // Si no hay postulación, usar el servicio de mail general
        const toArray = [to.trim()]; // El backend espera un array
        const ccArray = ccLine ? ccLine.split(',').map(e => e.trim()).filter(e => emailRx.test(e)) : [];
        
        // Si copyToMe está activado, agregar el correo del encargado al CC
        let finalCc = [...ccArray];
        if (copyToMe) {
          try {
            const ccDefaults = await getCcDefault();
            if (Array.isArray(ccDefaults) && ccDefaults.length > 0) {
              finalCc = [...new Set([...ccArray, ...ccDefaults])]; // Evitar duplicados
            }
          } catch (e) {
            console.warn('No se pudo obtener el CC por defecto:', e);
          }
        }
        
        const result = await sendEmail({
          to: toArray,
          cc: finalCc.length > 0 ? finalCc : undefined,
          subject: subject.trim(),
          message: body.trim(),
        });
        
        // Verificar si hubo un error en la respuesta
        if (result?.error || result?.success === false) {
          throw new Error(result?.message || 'Error al enviar el correo');
        }
      }

      // Obtener nombre de la empresa si hay postulación seleccionada
      let companyName = '';
      if (selectedApp?.id) {
        const updatedApp = selectedApp;
        companyName = updatedApp?.razonSocial || updatedApp?.nombreFantasia || 'Sin nombre';
      }

      await Swal.fire({
        icon: 'success',
        title: 'Enviado',
        html: companyName 
          ? `Correo "${subject.trim()}" enviado correctamente a ${to.trim()} para la empresa <strong>${companyName}</strong>.`
          : `Correo "${subject.trim()}" enviado correctamente a ${to.trim()}.`,
        timer: 2000,
        showConfirmButton: false,
      });
      
      // Cerrar sin verificar cambios (el envío ya se completó)
      setIsEditingUnlocked(false);
      onClose?.();
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo completar el envío.', 'error');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/30 backdrop-blur-sm flex items-center justify-center p-2 sm:p-3">
      <div className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF] text-white flex items-center justify-between flex-none">
          <div className="font-semibold inline-flex items-center gap-2">
            <Mail size={18} /> Plantillas de correo
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45]"
            title="Cerrar"
          >
            <XCircle size={18} />
          </button>
        </div>

        {/* Body scrolleable */}
        <div className="flex-1 overflow-y-auto">
          {/* Tabs sticky */}
          <div className="px-4 pt-3 pb-2 border-b bg-white sticky top-0 z-10">
            <div className="flex gap-2">
              <button
                onClick={() => setTab('SEND')}
                className={`px-4 py-2 rounded-t-lg border-b-2 ${tab==='SEND' ? 'border-[#0488AF] text-[#0488AF] font-semibold' : 'border-transparent text-slate-600'}`}
              >
                Correo personalizado
              </button>
              <button
                onClick={() => setTab('TEMPLATES')}
                className={`px-4 py-2 rounded-t-lg border-b-2 ${tab==='TEMPLATES' ? 'border-[#0488AF] text-[#0488AF] font-semibold' : 'border-transparent text-slate-600'}`}
              >
                Plantillas
              </button>
              <button
                onClick={() => setTab('CONFIG')}
                className={`px-4 py-2 rounded-t-lg border-b-2 ${tab==='CONFIG' ? 'border-[#0488AF] text-[#0488AF] font-semibold' : 'border-transparent text-slate-600'}`}
              >
                <Settings size={16} className="inline mr-1" />
                Configuración
              </button>
              <button
                onClick={() => setTab('PRESENTATION')}
                className={`px-4 py-2 rounded-t-lg border-b-2 ${tab==='PRESENTATION' ? 'border-[#0488AF] text-[#0488AF] font-semibold' : 'border-transparent text-slate-600'}`}
              >
                <FileText size={16} className="inline mr-1" />
                Ficha de Presentación
              </button>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-4 space-y-3">
            {tab === 'CONFIG' ? (
              <>
                <div className="text-[13px] text-slate-600 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                  <b>Configuración de correos:</b> Gestiona las direcciones de correo utilizadas en los emails de membresía. Para múltiples correos, sepáralos con comas. Puedes dejar campos vacíos si no deseas usarlos.
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
                  <span className="text-base leading-none">ℹ️</span>
                  <span>
                    <span className="font-bold text-blue-900">Importante:</span> Edita y guarda las direcciones de correo de a una. Al presionar "Guardar" solo se actualizará la dirección que estás editando.
                  </span>
                </div>
                
                {/* Banner de bloqueo de edición */}
                {!isConfigEditingUnlocked && (
                  <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-amber-700">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-amber-800">Edición bloqueada</div>
                          <div className="text-xs text-amber-700">Las configuraciones están protegidas. Haz clic en el botón para desbloquear y editar.</div>
                        </div>
                      </div>
                      <button
                        onClick={unlockConfigEditing}
                        className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-semibold text-sm"
                      >
                        Desbloquear edición
                      </button>
                    </div>
                  </div>
                )}
                
                {configLoading ? (
                  <div className="text-center py-12 text-gray-500">
                    <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                    Cargando configuraciones...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {emailConfigs.length > 0 ? (
                      emailConfigs.map((config) => {
                        const configKey = config.key === 'MEMBERSHIP_NOTIFY_TO' ? 'notify' : 
                                         config.key === 'MEMBERSHIP_REPLY_TO' ? 'reply' : 'cc';
                        const isExpanded = expandedConfigs[configKey];
                        
                        return (
                          <section key={config.id} className="rounded-xl border bg-gray-50">
                            <div 
                              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => setExpandedConfigs(prev => ({ ...prev, [configKey]: !prev[configKey] }))}
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                                <Mail size={16} className="text-[#0488AF]" />
                                <div className="text-sm font-semibold text-gray-900">
                                  {getConfigLabel(config.key)}
                                </div>
                                <TooltipHelper text={
                                  config.key === 'MEMBERSHIP_NOTIFY_TO'
                                    ? 'Direcciones de correo que recibirán notificaciones cuando se cree una nueva postulación. Separa múltiples emails con comas.'
                                    : config.key === 'MEMBERSHIP_CC_DEFAULT'
                                    ? 'Direcciones de correo que se agregarán automáticamente en copia (CC) cuando se envíen correos de membresía. Separa múltiples emails con comas.'
                                    : config.key === 'MEMBERSHIP_REPLY_TO'
                                    ? 'Dirección de correo que aparecerá como "Responder a" en los correos enviados. Debe ser un único email válido.'
                                    : (config.description || `Configuración de ${getConfigLabel(config.key)}`).replace(/punto y coma/gi, 'comas')
                                } />
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveConfig(config);
                                }}
                                disabled={configSaving[config.key] || editedConfigValues[config.key] === (config.value || '') || !isConfigEditingUnlocked}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg whitespace-nowrap bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed shrink-0 text-sm"
                              >
                                {configSaving[config.key] ? (
                                  <>
                                    <Loader2 className="animate-spin" size={14} />
                                    Guardando...
                                  </>
                                ) : (
                                  <>
                                    <Save size={14} />
                                    Guardar
                                  </>
                                )}
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="px-3 pb-3">
                                <input
                                  type="text"
                                  value={editedConfigValues[config.key] || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handleConfigValueChange(config.key, value);
                                    // Validar formato de email en tiempo real
                                    if (value.trim()) {
                                      if (config.key === 'MEMBERSHIP_REPLY_TO') {
                                        // Email único
                                        if (!emailRx.test(value.trim())) {
                                          // No mostrar error aquí, solo validar al guardar
                                        }
                                      } else if (config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT') {
                                        // Lista de emails
                                        const emails = value.split(',').map(e => e.trim()).filter(Boolean);
                                        const invalidEmails = emails.filter(email => !emailRx.test(email));
                                        // No mostrar error aquí, solo validar al guardar
                                      }
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const value = e.target.value.trim();
                                    if (value) {
                                      if (config.key === 'MEMBERSHIP_REPLY_TO') {
                                        if (!emailRx.test(value)) {
                                          Swal.fire({
                                            icon: 'warning',
                                            title: 'Formato de email inválido',
                                            text: 'El email debe contener @ y un dominio (ej: correo@empresa.cl)',
                                            timer: 3000,
                                            showConfirmButton: false,
                                          });
                                        }
                                      } else if (config.key === 'MEMBERSHIP_NOTIFY_TO' || config.key === 'MEMBERSHIP_CC_DEFAULT') {
                                        const emails = value.split(',').map(e => e.trim()).filter(Boolean);
                                        const invalidEmails = emails.filter(email => !emailRx.test(email));
                                        if (invalidEmails.length > 0) {
                                          Swal.fire({
                                            icon: 'warning',
                                            title: 'Formato de email inválido',
                                            text: `Los siguientes emails no tienen formato válido: ${invalidEmails.join(', ')}. Deben contener @ y un dominio.`,
                                            timer: 4000,
                                            showConfirmButton: false,
                                          });
                                        }
                                      }
                                    }
                                  }}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white"
                                  placeholder={getConfigPlaceholder(config.key)}
                                  disabled={configSaving[config.key] || !isConfigEditingUnlocked}
                                />
                              </div>
                            )}
                          </section>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No hay configuraciones disponibles. Las configuraciones se crearán automáticamente al iniciar el servidor.
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : tab === 'PRESENTATION' ? (
              <>
                <div className="rounded-xl border p-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Ficha de presentación (PDF)
                    </label>
                    {/* Hint con el mismo estilo que banco de minutas */}
                    <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm mb-3">
                      <span className="text-base leading-none">ℹ️</span>
                      <span>
                        Sube el archivo PDF de la presentación de CIDERE que se adjuntará cuando selecciones{" "}
                        <span className="font-bold text-blue-900">"Adjuntar ficha de presentación de CIDERE"</span> en correos personalizados.
                      </span>
                    </div>
                    {/* Mostrar archivo actual si existe */}
                    {loadingPresentation ? (
                      <div className="mt-2 text-center py-4 text-gray-500">
                        <Loader2 className="animate-spin mx-auto mb-2" size={20} />
                        <span className="text-sm">Cargando información del archivo...</span>
                      </div>
                    ) : presentationFileUrl ? (
                      <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText size={20} className="text-green-700 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-green-800">Ficha de presentación actual:</div>
                            <a 
                              href={presentationFileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-sm text-green-700 underline hover:no-underline truncate block"
                            >
                              Ver archivo
                            </a>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const result = await Swal.fire({
                              title: '¿Eliminar ficha de presentación?',
                              text: 'Esta acción eliminará la ficha de presentación actual. ¿Estás seguro?',
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonColor: '#dc2626',
                              cancelButtonColor: '#6b7280',
                              confirmButtonText: 'Sí, eliminar',
                              cancelButtonText: 'Cancelar',
                            });

                            if (result.isConfirmed) {
                              try {
                                setSending(true);
                                await deletePresentationFile();
                                setPresentationFileUrl('');
                                await Swal.fire({
                                  icon: 'success',
                                  title: 'Eliminado',
                                  text: 'Ficha de presentación eliminada correctamente.',
                                  timer: 2000,
                                  showConfirmButton: false,
                                });
                              } catch (error) {
                                Swal.fire('Error', error?.message || 'No se pudo eliminar la ficha de presentación.', 'error');
                              } finally {
                                setSending(false);
                              }
                            }
                          }}
                          disabled={sending}
                          className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 text-xs font-medium disabled:opacity-60 shrink-0"
                        >
                          Eliminar
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-600">
                        No hay ficha de presentación subida.
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (file.type !== 'application/pdf') {
                          await Swal.fire({
                            icon: 'error',
                            title: 'Formato inválido',
                            text: 'Debes subir un archivo PDF',
                          });
                          e.target.value = '';
                          return;
                        }
                        
                        if (file.size > 15 * 1024 * 1024) {
                          await Swal.fire({
                            icon: 'warning',
                            title: 'Archivo muy grande',
                            html: `El archivo es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
                            confirmButtonText: 'Entendido',
                          });
                          e.target.value = '';
                          return;
                        }
                        
                        // Confirmar antes de subir
                        const confirmResult = await Swal.fire({
                          title: '¿Subir ficha de presentación?',
                          html: `¿Estás seguro de subir el archivo <strong>"${file.name}"</strong> como ficha de presentación de CIDERE?<br/><br/><small>El archivo anterior será reemplazado automáticamente.</small>`,
                          icon: 'question',
                          showCancelButton: true,
                          confirmButtonColor: '#0488AF',
                          cancelButtonColor: '#6b7280',
                          confirmButtonText: 'Sí, subir',
                          cancelButtonText: 'Cancelar',
                        });
                        
                        if (!confirmResult.isConfirmed) {
                          e.target.value = '';
                          return;
                        }
                        
                        try {
                          setSending(true);
                          const uploadSwal = Swal.fire({
                            title: 'Subiendo...',
                            html: `Subiendo ficha de presentación<br/><small>${file.name}</small>`,
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            showConfirmButton: false,
                            didOpen: () => {
                              Swal.showLoading();
                            }
                          });
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const result = await uploadPresentationFile(formData);
                          
                          Swal.close();
                          
                          // Actualizar la URL del archivo
                          if (result.data?.url) {
                            setPresentationFileUrl(result.data.url);
                          }
                          
                          await Swal.fire({
                            icon: 'success',
                            title: 'Archivo subido',
                            text: 'La ficha de presentación se ha subido correctamente. El archivo anterior ha sido reemplazado.',
                            timer: 2000,
                            showConfirmButton: false,
                          });
                          
                          // Limpiar el input
                          e.target.value = '';
                        } catch (error) {
                          Swal.fire('Error', error?.message || 'No se pudo subir el archivo', 'error');
                        } finally {
                          setSending(false);
                        }
                      }}
                      className="mt-2 block w-full h-11 px-3 py-1.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0488AF]/10 file:text-[#0488AF] hover:file:bg-[#0488AF]/20 cursor-pointer"
                      disabled={sending}
                    />
                  </div>
                </div>
              </>
            ) : tab === 'TEMPLATES' ? (
              <>
                <div className="text-[13px] text-slate-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <b>Nota:</b> recuerda que los cambios en la plantilla se aplican a <b>todos</b> los posibles socios.
                </div>
                {/* Banner de bloqueo de edición */}
                {!isEditingUnlocked && (
                  <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-amber-700">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-amber-800">Edición bloqueada</div>
                          <div className="text-xs text-amber-700">Las plantillas están protegidas. Haz clic en el botón para desbloquear y editar.</div>
                        </div>
                      </div>
                      <button
                        onClick={unlockEditing}
                        className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-semibold text-sm"
                      >
                        Desbloquear edición
                      </button>
                    </div>
                  </div>
                )}

                {/* Toggle modo de edición */}
                {isEditingUnlocked && (
                  <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-gray-700">Modo de edición:</div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setEditMode('simple')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            editMode === 'simple'
                              ? 'bg-[#0488AF] text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Texto plano
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditMode('advanced')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            editMode === 'advanced'
                              ? 'bg-[#0488AF] text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Edición avanzada
                        </button>
                      </div>
                    </div>
                    <div className="text-[11px] text-gray-600">
                      {editMode === 'simple' 
                        ? 'Texto plano: Las variables ({{empresa}}, {{rut}}, etc.) están protegidas y no se pueden editar. Solo puedes editar el texto alrededor de las variables.'
                        : 'Modo avanzado: Puedes ver y editar todo, incluyendo las variables ({{empresa}}, {{rut}}, etc.) directamente en el texto.'}
                    </div>
                  </div>
                )}

                {/* Tokens - Solo visible en modo avanzado, sticky */}
                {isEditingUnlocked && editMode === 'advanced' && (
                  <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-3 sticky top-[60px] z-20 shadow-md">
                    <div className="text-xs font-semibold text-blue-800 mb-2">📎 Tokens disponibles (haz clic para insertar):</div>
                    <div className="flex flex-wrap gap-2">
                      {availableTokens.map((t) => (
                        <button
                          key={t.token}
                          type="button"
                          onClick={() => {
                            // Insertar en el campo activo
                            if (activeFieldRef.current === acceptSubjectRef.current) {
                              insertToken(t.token, acceptSubjectRef, (val) => setLocalAccept(s => ({ ...s, subject: val })));
                            } else if (activeFieldRef.current === acceptBodyRef.current) {
                              insertToken(t.token, acceptBodyRef, (val) => setLocalAccept(s => ({ ...s, body: val })));
                            } else if (activeFieldRef.current === rejectSubjectRef.current) {
                              insertToken(t.token, rejectSubjectRef, (val) => setLocalReject(s => ({ ...s, subject: val })));
                            } else if (activeFieldRef.current === rejectBodyRef.current) {
                              insertToken(t.token, rejectBodyRef, (val) => setLocalReject(s => ({ ...s, body: val })));
                            } else if (activeFieldRef.current === acceptanceSubjectRef.current) {
                              insertToken(t.token, acceptanceSubjectRef, (val) => setLocalAcceptance(s => ({ ...s, subject: val })));
                            } else if (activeFieldRef.current === acceptanceBodyRef.current) {
                              insertToken(t.token, acceptanceBodyRef, (val) => setLocalAcceptance(s => ({ ...s, body: val })));
                            } else if (activeFieldRef.current === receivedSubjectRef.current) {
                              insertToken(t.token, receivedSubjectRef, (val) => setLocalReceived(s => ({ ...s, subject: val })));
                            } else if (activeFieldRef.current === receivedBodyRef.current) {
                              insertToken(t.token, receivedBodyRef, (val) => setLocalReceived(s => ({ ...s, body: val })));
                            } else {
                              Swal.fire('Selecciona un campo', 'Haz clic en un campo de texto antes de insertar un token.', 'info');
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 cursor-pointer hover:scale-105 transition-transform ${
                            t.required 
                              ? 'bg-blue-100 border-blue-300 text-blue-800 font-semibold' 
                              : 'bg-white border-blue-200 text-blue-700'
                          }`}
                          title={`${t.description} - Se insertará: ${t.token}`}
                        >
                          <span className="text-xs font-medium">{t.label}</span>
                          {t.required && <span className="text-[10px] text-blue-600">(auto)</span>}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-blue-700">
                      💡 <strong>Tip:</strong> Haz clic en un campo de texto y luego en un token para insertarlo.
                    </div>
                  </div>
                )}

                {/* Recepción de Postulación */}
                <section className={`rounded-xl border ${!isEditingUnlocked ? 'opacity-60' : ''}`}>
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedTemplates(prev => ({ ...prev, received: !prev.received }))}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTemplates.received ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                      <div className="text-sm font-semibold text-slate-800">Plantilla: Recepción de Postulación</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingUnlocked && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveSingleTemplate('RECEIVED', 'Recepción de Postulación');
                          }}
                          className="h-8 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 text-sm"
                          title="Guardar cambios de esta plantilla"
                        >
                          <Save size={14} />
                          Guardar cambios
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreReceivedTemplate();
                        }}
                        disabled={!isEditingUnlocked}
                        className={`h-8 px-3 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] inline-flex items-center gap-1.5 text-sm ${!isEditingUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Restaurar plantilla de Recepción al estado original"
                      >
                        <RotateCcw size={14} />
                        Restaurar original
                      </button>
                    </div>
                  </div>
                  {expandedTemplates.received && (
                    <>
                      <div className="px-3 pb-3">
                        <div className="text-xs text-slate-600 mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                          Esta plantilla se usa para notificar a los postulantes que se ha recibido su postulación. Se envía automáticamente cuando se crea una nueva postulación.
                        </div>
                        <div className="grid gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Asunto
                        <TooltipHelper text="El asunto del correo que se enviará cuando se reciba una postulación. Puedes usar variables como {{empresa}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <input
                        ref={receivedSubjectRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 h-10 ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        value={localReceived.subject || ''}
                        onFocus={() => { activeFieldRef.current = receivedSubjectRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.receivedSubject = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localReceived.subject || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.receivedSubject = pos;
                          }
                        }}
                        onInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            if (cursorPosBeforeChange.current.receivedSubject === 0 && e.target.selectionStart > 0) {
                              const currentValue = e.target.value || '';
                              const originalValue = localReceived.subject || '';
                              if (currentValue.length > originalValue.length) {
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.receivedSubject = pos;
                              } else {
                                cursorPosBeforeChange.current.receivedSubject = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localReceived.subject || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.receivedSubject;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, receivedSubjectRef);
                            
                            if (protectedText !== newText) {
                              setLocalReceived(s => ({ ...s, subject: protectedText }));
                              setTimeout(() => {
                                if (receivedSubjectRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  receivedSubjectRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              setLocalReceived(s => ({ ...s, subject: newText }));
                              setTimeout(() => {
                                if (receivedSubjectRef.current) {
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  receivedSubjectRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            setLocalReceived(s => ({ ...s, subject: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el asunto y haz clic en los tokens para insertarlos" : "Escribe el asunto"}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Cuerpo
                        <TooltipHelper text="El cuerpo del correo de recepción de postulación. Puedes usar variables como {{empresa}}, {{rut}}, {{contactoNombre}} y {{fecha}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <textarea
                        ref={receivedBodyRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        rows={8}
                        value={localReceived.body || ''}
                        onFocus={() => { activeFieldRef.current = receivedBodyRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.receivedBody = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localReceived.body || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.receivedBody = pos;
                          }
                        }}
                        onInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            if (cursorPosBeforeChange.current.receivedBody === 0 && e.target.selectionStart > 0) {
                              const currentValue = e.target.value || '';
                              const originalValue = localReceived.body || '';
                              if (currentValue.length > originalValue.length) {
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.receivedBody = pos;
                              } else {
                                cursorPosBeforeChange.current.receivedBody = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localReceived.body || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.receivedBody;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, receivedBodyRef);
                            
                            if (protectedText !== newText) {
                              setLocalReceived(s => ({ ...s, body: protectedText }));
                              setTimeout(() => {
                                if (receivedBodyRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  receivedBodyRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              setLocalReceived(s => ({ ...s, body: newText }));
                              setTimeout(() => {
                                if (receivedBodyRef.current) {
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  receivedBodyRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            setLocalReceived(s => ({ ...s, body: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el cuerpo y haz clic en los tokens para insertarlos" : "Escribe el cuerpo"}
                      />
                    </label>
                    
                    {/* Checkbox para adjuntar presentación */}
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={receivedAttachPresentation}
                          onChange={(e) => setReceivedAttachPresentation(e.target.checked)}
                          disabled={!isEditingUnlocked}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">
                          Adjuntar ficha de presentación de CIDERE en correos de recepción
                        </span>
                      </label>
                      <div className="mt-2 text-xs text-gray-600">
                        Si está activado, se adjuntará automáticamente la ficha de presentación de CIDERE cuando se envíe el correo de recepción de postulación.
                      </div>
                    </div>
                        </div>
                      </div>
                    </>
                  )}
                </section>

                {/* Aceptación (Declaración Jurada) */}
                <section className={`rounded-xl border ${!isEditingUnlocked ? 'opacity-60' : ''}`}>
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedTemplates(prev => ({ ...prev, accept: !prev.accept }))}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTemplates.accept ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                      <div className="text-sm font-semibold text-slate-800">Plantilla: Envío de Declaración Jurada</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingUnlocked && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveSingleTemplate('ACCEPTED', 'Envío de Declaración Jurada');
                          }}
                          className="h-8 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 text-sm"
                          title="Guardar cambios de esta plantilla"
                        >
                          <Save size={14} />
                          Guardar cambios
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreAcceptTemplate();
                        }}
                        disabled={!isEditingUnlocked}
                        className={`h-8 px-3 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] inline-flex items-center gap-1.5 text-sm ${!isEditingUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Restaurar plantilla de Aceptación al estado original"
                      >
                        <RotateCcw size={14} />
                        Restaurar original
                      </button>
                    </div>
                  </div>
                  {expandedTemplates.accept && (
                    <div className="px-3 pb-3">
                      <div className="grid gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Asunto
                        <TooltipHelper text="El asunto del correo que se enviará cuando se envíe la declaración jurada (cambia a WAITING_RESPONSE). Puedes usar variables como {{empresa}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <input
                        ref={acceptSubjectRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 h-10 ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        value={localAccept.subject || ''}
                        onFocus={() => { activeFieldRef.current = acceptSubjectRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            // Guardar posición del cursor ANTES del cambio
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.subject = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localAccept.subject || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          // Guardar posición antes del cambio (para casos como pegar)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.subject = pos;
                          }
                        }}
                        onInput={(e) => {
                          // También guardar en onInput como respaldo (se ejecuta antes de onChange)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            // Solo actualizar si no se guardó en onKeyDown (por ejemplo, al pegar)
                            if (cursorPosBeforeChange.current.subject === 0 && e.target.selectionStart > 0) {
                              // Intentar calcular la posición antes del cambio basándose en la diferencia de longitud
                              const currentValue = e.target.value || '';
                              const originalValue = localAccept.subject || '';
                              if (currentValue.length > originalValue.length) {
                                // Se insertó texto, la posición antes sería la actual menos la diferencia
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.subject = pos;
                              } else {
                                cursorPosBeforeChange.current.subject = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localAccept.subject || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.subject;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, acceptSubjectRef);
                            
                            if (protectedText !== newText) {
                              // Se intentó modificar una variable, restaurar
                              setLocalAccept(s => ({ ...s, subject: protectedText }));
                              setTimeout(() => {
                                if (acceptSubjectRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  acceptSubjectRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              // Cambio permitido - actualizar y usar la posición del cursor que el navegador calculó
                              setLocalAccept(s => ({ ...s, subject: newText }));
                              setTimeout(() => {
                                if (acceptSubjectRef.current) {
                                  // Usar la posición del cursor que el navegador ya calculó
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  acceptSubjectRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            // Modo avanzado o no desbloqueado: permitir todo
                            setLocalAccept(s => ({ ...s, subject: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el asunto y haz clic en los tokens para insertarlos" : "Escribe el asunto"}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Cuerpo
                        <TooltipHelper text="El cuerpo del correo que se envía con la declaración jurada. Puedes usar variables como {{empresa}}, {{rut}}, {{contactoNombre}} y {{fecha}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <textarea
                        ref={acceptBodyRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        rows={8}
                        value={localAccept.body || ''}
                        onFocus={() => { activeFieldRef.current = acceptBodyRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            // Guardar posición del cursor ANTES del cambio
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.body = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localAccept.body || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          // Guardar posición antes del cambio (para casos como pegar)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.body = pos;
                          }
                        }}
                        onInput={(e) => {
                          // También guardar en onInput como respaldo (se ejecuta antes de onChange)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            // Solo actualizar si no se guardó en onKeyDown (por ejemplo, al pegar)
                            if (cursorPosBeforeChange.current.body === 0 && e.target.selectionStart > 0) {
                              // Intentar calcular la posición antes del cambio basándose en la diferencia de longitud
                              const currentValue = e.target.value || '';
                              const originalValue = localAccept.body || '';
                              if (currentValue.length > originalValue.length) {
                                // Se insertó texto, la posición antes sería la actual menos la diferencia
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.body = pos;
                              } else {
                                cursorPosBeforeChange.current.body = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localAccept.body || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.body;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, acceptBodyRef);
                            
                            if (protectedText !== newText) {
                              // Se intentó modificar una variable, restaurar
                              setLocalAccept(s => ({ ...s, body: protectedText }));
                              setTimeout(() => {
                                if (acceptBodyRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  acceptBodyRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              // Cambio permitido - actualizar y usar la posición del cursor que el navegador calculó
                              setLocalAccept(s => ({ ...s, body: newText }));
                              setTimeout(() => {
                                if (acceptBodyRef.current) {
                                  // Usar la posición del cursor que el navegador ya calculó
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  acceptBodyRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            // Modo avanzado o no desbloqueado: permitir todo
                            setLocalAccept(s => ({ ...s, body: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el cuerpo y haz clic en los tokens para insertarlos" : "Escribe el cuerpo"}
                      />
                    </label>
                      </div>
                    </div>
                  )}
                </section>

                {/* Rechazo */}
                <section className={`rounded-xl border ${!isEditingUnlocked ? 'opacity-60' : ''}`}>
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedTemplates(prev => ({ ...prev, reject: !prev.reject }))}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTemplates.reject ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                      <div className="text-sm font-semibold text-slate-800">Plantilla: Rechazo</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingUnlocked && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveSingleTemplate('REJECTED', 'Rechazo');
                          }}
                          className="h-8 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 text-sm"
                          title="Guardar cambios de esta plantilla"
                        >
                          <Save size={14} />
                          Guardar cambios
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreRejectTemplate();
                        }}
                        disabled={!isEditingUnlocked}
                        className={`h-8 px-3 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] inline-flex items-center gap-1.5 text-sm ${!isEditingUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Restaurar plantilla de Rechazo al estado original"
                      >
                        <RotateCcw size={14} />
                        Restaurar original
                      </button>
                    </div>
                  </div>
                  {expandedTemplates.reject && (
                    <div className="px-3 pb-3">
                      <div className="grid gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Asunto
                        <TooltipHelper text="El asunto del correo que se enviará cuando se rechace una postulación. Puedes usar variables como {{empresa}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <input
                        ref={rejectSubjectRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 h-10 ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        value={localReject.subject || ''}
                        onFocus={() => { activeFieldRef.current = rejectSubjectRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            // Guardar posición del cursor ANTES del cambio
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.rejectSubject = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localReject.subject || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          // Guardar posición antes del cambio (para casos como pegar)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.rejectSubject = pos;
                          }
                        }}
                        onInput={(e) => {
                          // También guardar en onInput como respaldo (se ejecuta antes de onChange)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            // Solo actualizar si no se guardó en onKeyDown (por ejemplo, al pegar)
                            if (cursorPosBeforeChange.current.rejectSubject === 0 && e.target.selectionStart > 0) {
                              // Intentar calcular la posición antes del cambio basándose en la diferencia de longitud
                              const currentValue = e.target.value || '';
                              const originalValue = localReject.subject || '';
                              if (currentValue.length > originalValue.length) {
                                // Se insertó texto, la posición antes sería la actual menos la diferencia
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.rejectSubject = pos;
                              } else {
                                cursorPosBeforeChange.current.rejectSubject = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localReject.subject || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.rejectSubject;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, rejectSubjectRef);
                            
                            if (protectedText !== newText) {
                              // Se intentó modificar una variable, restaurar
                              setLocalReject(s => ({ ...s, subject: protectedText }));
                              setTimeout(() => {
                                if (rejectSubjectRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  rejectSubjectRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              // Cambio permitido - actualizar y usar la posición del cursor que el navegador calculó
                              setLocalReject(s => ({ ...s, subject: newText }));
                              setTimeout(() => {
                                if (rejectSubjectRef.current) {
                                  // Usar la posición del cursor que el navegador ya calculó
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  rejectSubjectRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            // Modo avanzado o no desbloqueado: permitir todo
                            setLocalReject(s => ({ ...s, subject: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el asunto y haz clic en los tokens para insertarlos" : "Escribe el asunto"}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Cuerpo
                        <TooltipHelper text="El cuerpo del correo de rechazo. Puedes usar variables como {{empresa}}, {{rut}}, {{contactoNombre}} y {{fecha}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <textarea
                        ref={rejectBodyRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        rows={8}
                        value={localReject.body || ''}
                        onFocus={() => { activeFieldRef.current = rejectBodyRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            // Guardar posición del cursor ANTES del cambio
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.rejectBody = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localReject.body || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          // Guardar posición antes del cambio (para casos como pegar)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.rejectBody = pos;
                          }
                        }}
                        onInput={(e) => {
                          // También guardar en onInput como respaldo (se ejecuta antes de onChange)
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            // Solo actualizar si no se guardó en onKeyDown (por ejemplo, al pegar)
                            if (cursorPosBeforeChange.current.rejectBody === 0 && e.target.selectionStart > 0) {
                              // Intentar calcular la posición antes del cambio basándose en la diferencia de longitud
                              const currentValue = e.target.value || '';
                              const originalValue = localReject.body || '';
                              if (currentValue.length > originalValue.length) {
                                // Se insertó texto, la posición antes sería la actual menos la diferencia
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.rejectBody = pos;
                              } else {
                                cursorPosBeforeChange.current.rejectBody = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localReject.body || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.rejectBody;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, rejectBodyRef);
                            
                            if (protectedText !== newText) {
                              // Se intentó modificar una variable, restaurar
                              setLocalReject(s => ({ ...s, body: protectedText }));
                              setTimeout(() => {
                                if (rejectBodyRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  rejectBodyRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              // Cambio permitido - actualizar y usar la posición del cursor que el navegador calculó
                              setLocalReject(s => ({ ...s, body: newText }));
                              setTimeout(() => {
                                if (rejectBodyRef.current) {
                                  // Usar la posición del cursor que el navegador ya calculó
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  rejectBodyRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            // Modo avanzado o no desbloqueado: permitir todo
                            setLocalReject(s => ({ ...s, body: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el cuerpo y haz clic en los tokens para insertarlos" : "Escribe el cuerpo"}
                      />
                    </label>
                      </div>
                    </div>
                  )}
                </section>

                {/* Aceptación Final */}
                <section className={`rounded-xl border ${!isEditingUnlocked ? 'opacity-60' : ''}`}>
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedTemplates(prev => ({ ...prev, acceptance: !prev.acceptance }))}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTemplates.acceptance ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                      <div className="text-sm font-semibold text-slate-800">Plantilla: Aceptación Final</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingUnlocked && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveSingleTemplate('ACCEPTANCE', 'Aceptación Final');
                          }}
                          className="h-8 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 text-sm"
                          title="Guardar cambios de esta plantilla"
                        >
                          <Save size={14} />
                          Guardar cambios
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreAcceptanceTemplate();
                        }}
                        disabled={!isEditingUnlocked}
                        className={`h-8 px-3 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] inline-flex items-center gap-1.5 text-sm ${!isEditingUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Restaurar plantilla de Aceptación Final al estado original"
                      >
                        <RotateCcw size={14} />
                        Restaurar original
                      </button>
                    </div>
                  </div>
                  {expandedTemplates.acceptance && (
                    <div className="px-3 pb-3">
                      <div className="text-xs text-slate-600 mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        Esta plantilla se usa cuando se envía el correo de aceptación final (cuando el estado ya es ACCEPTED). No incluye la declaración jurada.
                      </div>
                      <div className="grid gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Asunto
                        <TooltipHelper text="El asunto del correo que se enviará cuando se confirme la aceptación final. Puedes usar variables como {{empresa}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <input
                        ref={acceptanceSubjectRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 h-10 ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        value={localAcceptance.subject || ''}
                        onFocus={() => { activeFieldRef.current = acceptanceSubjectRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.acceptanceSubject = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localAcceptance.subject || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.acceptanceSubject = pos;
                          }
                        }}
                        onInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            if (cursorPosBeforeChange.current.acceptanceSubject === 0 && e.target.selectionStart > 0) {
                              const currentValue = e.target.value || '';
                              const originalValue = localAcceptance.subject || '';
                              if (currentValue.length > originalValue.length) {
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.acceptanceSubject = pos;
                              } else {
                                cursorPosBeforeChange.current.acceptanceSubject = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localAcceptance.subject || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.acceptanceSubject;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, acceptanceSubjectRef);
                            
                            if (protectedText !== newText) {
                              setLocalAcceptance(s => ({ ...s, subject: protectedText }));
                              setTimeout(() => {
                                if (acceptanceSubjectRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  acceptanceSubjectRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              setLocalAcceptance(s => ({ ...s, subject: newText }));
                              setTimeout(() => {
                                if (acceptanceSubjectRef.current) {
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  acceptanceSubjectRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            setLocalAcceptance(s => ({ ...s, subject: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el asunto y haz clic en los tokens para insertarlos" : "Escribe el asunto"}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Cuerpo
                        <TooltipHelper text="El cuerpo del correo de aceptación final. Puedes usar variables como {{empresa}}, {{rut}}, {{contactoNombre}} y {{fecha}} para personalizar el mensaje." />
                        {editMode === 'simple' && isEditingUnlocked && (
                          <span className="ml-2 text-[10px] text-blue-600">(Variables protegidas - solo edita el texto alrededor)</span>
                        )}
                      </div>
                      <textarea
                        ref={acceptanceBodyRef}
                        disabled={!isEditingUnlocked}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${!isEditingUnlocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${editMode === 'simple' && isEditingUnlocked ? 'bg-blue-50' : ''}`}
                        rows={8}
                        value={localAcceptance.body || ''}
                        onFocus={() => { activeFieldRef.current = acceptanceBodyRef.current; }}
                        onKeyDown={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const currentPos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.acceptanceBody = currentPos;
                            if ((e.key === 'Backspace' || e.key === 'Delete') && isPositionInVariable(localAcceptance.body || '', currentPos)) {
                              e.preventDefault();
                            }
                          }
                        }}
                        onBeforeInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            const pos = e.target.selectionStart || 0;
                            cursorPosBeforeChange.current.acceptanceBody = pos;
                          }
                        }}
                        onInput={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked && e.target) {
                            if (cursorPosBeforeChange.current.acceptanceBody === 0 && e.target.selectionStart > 0) {
                              const currentValue = e.target.value || '';
                              const originalValue = localAcceptance.body || '';
                              if (currentValue.length > originalValue.length) {
                                const diff = currentValue.length - originalValue.length;
                                const pos = Math.max(0, (e.target.selectionStart || 0) - diff);
                                cursorPosBeforeChange.current.acceptanceBody = pos;
                              } else {
                                cursorPosBeforeChange.current.acceptanceBody = e.target.selectionStart || 0;
                              }
                            }
                          }
                        }}
                        onChange={(e) => {
                          if (editMode === 'simple' && isEditingUnlocked) {
                            const originalText = localAcceptance.body || '';
                            const newText = e.target.value;
                            const savedCursorPos = cursorPosBeforeChange.current.acceptanceBody;
                            const browserCursorPos = e.target.selectionStart || 0;
                            
                            const protectedText = handleSimpleModeEdit(originalText, newText, savedCursorPos, acceptanceBodyRef);
                            
                            if (protectedText !== newText) {
                              setLocalAcceptance(s => ({ ...s, body: protectedText }));
                              setTimeout(() => {
                                if (acceptanceBodyRef.current) {
                                  const restorePos = Math.min(savedCursorPos, protectedText.length);
                                  acceptanceBodyRef.current.setSelectionRange(restorePos, restorePos);
                                }
                              }, 0);
                            } else {
                              setLocalAcceptance(s => ({ ...s, body: newText }));
                              setTimeout(() => {
                                if (acceptanceBodyRef.current) {
                                  const finalPos = Math.min(browserCursorPos, newText.length);
                                  acceptanceBodyRef.current.setSelectionRange(finalPos, finalPos);
                                }
                              }, 0);
                            }
                          } else {
                            setLocalAcceptance(s => ({ ...s, body: e.target.value }));
                          }
                        }}
                        placeholder={editMode === 'advanced' ? "Escribe el cuerpo y haz clic en los tokens para insertarlos" : "Escribe el cuerpo"}
                      />
                    </label>
                      </div>
                    </div>
                  )}
                </section>
              </>
            ) : (
              <>
                {/* Buscar postulación (opcional) */}
                <div className="rounded-xl border p-3">
                  <div className="text-xs text-slate-600 mb-1">Buscar postulación (opcional)</div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      value={qSend}
                      onChange={(e) => setQSend(e.target.value)}
                      placeholder="Razón social, RUT, contacto…"
                      className="w-full h-10 pl-10 pr-3 rounded-lg border"
                    />
                  </div>

                  <div className="mt-3 max-h-52 overflow-auto border rounded-lg">
                    {loading ? (
                      <div className="py-6 text-center text-gray-500">
                        <Loader2 className="inline animate-spin mr-2" /> Buscando…
                      </div>
                    ) : (apps || []).length === 0 ? (
                      <div className="py-6 text-center text-gray-500">Sin resultados</div>
                    ) : (
                      <table className="w-full text-sm">
                        <tbody>
                          {apps.map(a => (
                            <tr key={a.id} className="border-b last:border-b-0">
                              <td className="px-3 py-2">
                                <div className="font-semibold text-[#0488AF] truncate max-w-[260px]">
                                  {a.razonSocial || a.nombreFantasia || 'Sin nombre'}
                                </div>
                                <div className="text-xs text-slate-600 truncate">{a.rutEmpresa || '—'}</div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => setSelectedApp(a)}
                                  className="px-3 h-8 rounded-md border bg-white hover:bg-gray-50"
                                >
                                  Seleccionar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {selectedApp && (
                    <div className="mt-3 text-xs text-slate-700">
                      <div className="font-semibold">Seleccionado:</div>
                      <div>{selectedApp.razonSocial || selectedApp.nombreFantasia || 'Sin nombre'} — {selectedApp.rutEmpresa || '—'}</div>
                      <button
                        onClick={() => setSelectedApp(null)}
                        className="mt-1 text-[12px] text-rose-600 hover:underline"
                      >
                        Quitar selección
                      </button>
                    </div>
                  )}
                </div>

                {/* Form de envío manual */}
                <div className="rounded-xl border p-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Para
                        <TooltipHelper text="Dirección de correo electrónico del destinatario principal. Se autocompleta al seleccionar una postulación (opcional). Solo puedes ingresar un destinatario en este campo." />
                      </div>
                      <input
                        className={`w-full rounded-lg border px-3 h-10 ${toError ? 'border-red-500 bg-red-50' : ''}`}
                        type="email"
                        value={to}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTo(value);
                          // Validar en tiempo real
                          if (value.trim() && !emailRx.test(value.trim())) {
                            setToError('El formato del email no es válido. Debe contener @ y un dominio (ej: correo@empresa.cl)');
                          } else {
                            setToError('');
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value && !emailRx.test(value)) {
                            setToError('El formato del email no es válido. Debe contener @ y un dominio (ej: correo@empresa.cl)');
                          } else {
                            setToError('');
                          }
                        }}
                        placeholder="correo@empresa.cl"
                      />
                      {toError && (
                        <div className="mt-1 text-xs text-red-600">{toError}</div>
                      )}
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        CC (opcional)
                        <TooltipHelper text="Direcciones de correo para copia. Separa múltiples correos con comas." />
                      </div>
                      <input
                        className={`w-full rounded-lg border px-3 h-10 ${ccLineError ? 'border-red-500 bg-red-50' : ''}`}
                        type="text"
                        value={ccLine}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCcLine(value);
                          // Validar en tiempo real si hay contenido
                          if (value.trim()) {
                            const ccEmails = value.split(',').map(e => e.trim()).filter(Boolean);
                            const invalidEmails = ccEmails.filter(email => !emailRx.test(email));
                            if (invalidEmails.length > 0) {
                              setCcLineError(`Los siguientes emails no tienen formato válido: ${invalidEmails.join(', ')}. Deben contener @ y un dominio.`);
                            } else {
                              setCcLineError('');
                            }
                          } else {
                            setCcLineError('');
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value) {
                            const ccEmails = value.split(',').map(e => e.trim()).filter(Boolean);
                            const invalidEmails = ccEmails.filter(email => !emailRx.test(email));
                            if (invalidEmails.length > 0) {
                              setCcLineError(`Los siguientes emails no tienen formato válido: ${invalidEmails.join(', ')}. Deben contener @ y un dominio.`);
                            } else {
                              setCcLineError('');
                            }
                          } else {
                            setCcLineError('');
                          }
                        }}
                        placeholder="cc1@dom.com, cc2@dom.com"
                      />
                      {ccLineError ? (
                        <div className="mt-1.5 text-[11px] text-red-600">{ccLineError}</div>
                      ) : (
                        <div className="mt-1.5 text-[11px] text-gray-500">
                          Separa múltiples correos con comas.
                        </div>
                      )}
                    </label>

                    <label className="block md:col-span-2">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Asunto
                        <TooltipHelper text="Asunto del correo personalizado. Si seleccionaste una postulación, se enviará asociado a ella; si no, será un correo general." />
                      </div>
                      <input
                        className="w-full rounded-lg border px-3 h-10"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Asunto del correo"
                      />
                    </label>

                    <label className="block md:col-span-2">
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        Cuerpo
                        <TooltipHelper text="Cuerpo del correo personalizado. Puedes escribir el mensaje completo. Si seleccionaste una postulación, se enviará asociado a ella; si no, será un correo general." />
                      </div>
                      <textarea
                        className="w-full rounded-lg border px-3 py-2 text-sm"
                        rows={9}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="mt-3 rounded-xl border p-3 bg-slate-50">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Opciones</div>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={copyToMe}
                        onChange={(e) => setCopyToMe(e.target.checked)}
                      />
                      <span className="text-sm text-gray-700">Con copia al correo del encargado</span>
                    </label>

                    {selectedApp && (
                      <>
                        <label className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            checked={attachDeclaration}
                            onChange={(e) => setAttachDeclaration(e.target.checked)}
                            disabled={!selectedApp}
                          />
                          <span className="text-sm text-gray-700">Adjuntar declaración jurada</span>
                        </label>

                        <label className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            checked={attachPdf}
                            onChange={(e) => setAttachPdf(e.target.checked)}
                          />
                          <span className="text-sm text-gray-700">Adjuntar ficha de presentación de CIDERE</span>
                        </label>
                      </>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={sendFromTemplates}
                      disabled={sending}
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm"
                    >
                      {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                      Enviar correo
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer (solo para plantillas) */}
        {tab === 'TEMPLATES' && (
          <div className="px-4 py-3 flex justify-end gap-2 border-t bg-gray-50 flex-none">
            <button 
              onClick={handleClose}
              className="h-10 px-4 rounded-lg border bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
